/**
 * ./src/modules/shopping-list/components/AddToShoppingListButton.tsx
 *
 * AddToShoppingListButton component
 *
 * @copyright DesignShop, 2024
 * @since 07/16/2024
 */
import { cloneElement } from 'react';

import { Button, ButtonProps } from '@/modules/common/components/Button';
import { Heart2 } from '@/modules/common/components/icons/Heart2';
import { cn } from '@/modules/common/lib/utils';

import {
  ShoppingListActionCallbacks,
  ShoppingListSaveType,
  useShoppingListActions,
} from '../hooks/useShoppingListActions';
import { ProductShoppingList } from '../services/shoppingListAPI';

export interface AddToShoppingListButtonProps extends ButtonProps {
  sku?: string;
  quantity?: number;
  textLineItem?: {
    key: string;
    name: string;
    saveType: string;
  };
  image?: string;
  color?: string;
  saveType?: ShoppingListSaveType;
  callbacks?: ShoppingListActionCallbacks;
  brandSku?: string;
  brandId?: string;
  bulkProducts?: ProductShoppingList[];
}

export const AddToShoppingListButton = ({
  children,
  className,
  sku,
  quantity = 1,
  textLineItem,
  image,
  color,
  saveType = ShoppingListSaveType.Product,
  callbacks,
  brandSku,
  brandId,
  variant = 'ghost',
  bulkProducts,
}: AddToShoppingListButtonProps) => {
  const { isOnShoppingList, handleAdd, handleRemove, isLoading } = useShoppingListActions({
    sku,
    quantity,
    textLineItem,
    image,
    color,
    type: saveType,
    callbacks,
    brandSku,
    brandId,
    bulkProducts,
  });

  return (
    <Button
      aria-label="Add to shopping list"
      variant={variant}
      className={cn(
        'absolute right-4 top-4 h-8 w-8 p-0 !text-black transition-all disabled:opacity-65',
        className
      )}
      onClick={isOnShoppingList ? handleRemove : handleAdd}
      type="button"
      disabled={isLoading}
      data-testid="add-to-shopping-list-button"
      id="add-to-shopping-list-button"
    >
      {children ?
        cloneElement(children as React.ReactElement, {
          ...((children as React.ReactElement).props as React.SVGProps<SVGSVGElement>),
          ...(isOnShoppingList && { backgroundColor: 'black' }),
        })
      : <Heart2
          innerOpacity={isOnShoppingList ? 1 : undefined}
          className={isLoading ? 'animate-heartbeat' : ''}
        />
      }
    </Button>
  );
};
